<template>
  <Drawer ref="drawer" content-padding="px-0 py-0" :show-footer="true" @hide="hideDrawer">
    <template #activator="{ show: showDrawer }">
      <slot name="activator" :show="showDrawer">
        <FloatingMenu :offset="[54, -8]" :arrow="false" :placement="'bottom-start'">
          <template #activator="{ show: showMenu }">
            <button
              :class="props.class"
              class="text-secondary inline-flex items-center gap-1 text-base hover:bg-light-neutral-300 text-secondary-500"
              @click.stop="showMenu"
            >
              <PlusIcon class="w-4 h-4" />
              <span class="hover:cursor-pointer text-base leading-5"> Add Review </span>
            </button>
          </template>
          <template #content>
            <div class="w-[200px]">
              <button
                class="flex gap-2 px-5 py-3 items-center"
                @click.stop="showCreateReviewDrawer(true, showDrawer)"
              >
                <CpuChipIcon class="size-6 text-mid-neutral-300" aria-hidden="true" />
                <span
                  class="hover:cursor-pointer text-sm font-medium leading-5 text-dark-neutral-500"
                >
                  Automated
                </span>
              </button>
              <button
                class="flex gap-2 px-5 py-3 items-center"
                @click.stop="showCreateReviewDrawer(false, showDrawer)"
              >
                <HandRaisedIcon class="size-6 text-mid-neutral-300" />
                <span
                  class="hover:cursor-pointer text-sm font-medium leading-5 text-dark-neutral-500"
                >
                  Manual
                </span>
              </button>
            </div>
          </template>
        </FloatingMenu>
      </slot>
    </template>

    <template #header="{ hide }">
      <div class="flex items-center gap-2">
        <button
          v-if="reviewCreate.isReviewAutomated.value"
          class="text-mid-neutral-500 text-base rounded-full"
          :disabled="
            reviewCreate.currentStep.value === CreateReviewStep.PresetSelection || isLoading
          "
          :class="[
            reviewCreate.currentStep.value === CreateReviewStep.PresetSelection || isLoading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-mid-neutral-300',
          ]"
          @click="reviewCreate.goToPreviousStep"
        >
          <div
            class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
          >
            <ChevronLeftIcon class="stroke-[2.5px] text-mid-neutral-500 size-4" />
          </div>
        </button>
        <p
          class="text-dark-neutral-500 text-lg font-semibold capitalize text-center flex gap-2 items-center"
        >
          Review wizard
        </p>
      </div>

      <button
        class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
        :disabled="isLoading"
        :class="{
          'opacity-50 cursor-not-allowed': isLoading,
          'hover:bg-mid-neutral-100': !isLoading,
        }"
        @click="hide"
      >
        <XIcon class="size-4 stroke-2 text-mid-neutral-500" />
      </button>
    </template>

    <template #content="{ hide }">
      <div class="flex-1 overflow-auto">
        <div v-if="isLoading && loadingMessages.length" class="py-4 px-6">
          <div
            v-for="(msg, index) in loadingMessages.slice(0, -1)"
            :key="index"
            class="flex items-center gap-2 text-mid-neutral-500 pl-12"
          >
            {{ msg }}
          </div>
          <Loader
            :key="loadingMessages.length"
            :message="loadingMessages[loadingMessages.length - 1]"
          />
        </div>
        <KeepAlive v-else>
          <component :is="currentStepComponent" @create-review="createReview(hide)" />
        </KeepAlive>
      </div>
    </template>

    <template #footer="{ hide }">
      <div class="flex justify-end">
        <button
          v-if="
            [CreateReviewStep.Summary, CreateReviewStep.AssistantsSettings].includes(
              reviewCreate.currentStep.value,
            )
          "
          class="bg-primary-500 text-white rounded-md text-base tracking-tighter px-4 py-2 font-medium"
          :disabled="!reviewCreate.isCurrentStepValid.value || isLoading"
          :class="{
            'opacity-50 cursor-not-allowed': !reviewCreate.isCurrentStepValid.value || isLoading,
            'hover:brightness-125': reviewCreate.isCurrentStepValid.value && !isLoading,
          }"
          @click="createReview(hide)"
        >
          Confirm
        </button>
        <button
          v-if="
            [
              CreateReviewStep.MedicalBackgroundIndication,
              CreateReviewStep.PmsDeviceLevelMinimal,
              CreateReviewStep.NotifiedBody,
            ].includes(reviewCreate.currentStep.value)
          "
          class="bg-primary-500 text-white rounded-md text-base tracking-tighter px-4 py-2 font-medium"
          :disabled="!reviewCreate.isCurrentStepValid.value || isLoading"
          :class="{
            'opacity-50 cursor-not-allowed': !reviewCreate.isCurrentStepValid.value || isLoading,
            'hover:brightness-125': reviewCreate.isCurrentStepValid.value && !isLoading,
          }"
          @click="reviewCreate.goToNextStep()"
        >
          Next
        </button>
      </div>
    </template>
  </Drawer>
</template>

<script lang="ts" setup>
import { Id } from "@core/domain/types/id.type";
import { hideAll } from "tippy.js";
import { provide, computed, ref } from "vue";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";
import XIcon from "@app/components/Icons/XIcon.vue";
import useCreateProjectReview, {
  CreateReviewKey,
  CreateReviewStep,
} from "./useCreateProjectReview";
import useProjects from "../../use-projects";
import PresetSelection from "./ReviewPreset/PresetSelection.vue";
import Summary from "./Summary/Summary.vue";
import DeviceSpecificSearchMinimalPreset from "./ReviewPreset/Presets/DeviceSpecificSearchPreset/DeviceSpecificSearchMinimalPreset.vue";
import { useRouter } from "vue-router";
import { ReviewPreset } from "@core/domain/types/review-preset.type";
import useLoading from "@app/composables/use-loading";
import MedicalBackgroundIndication from "./ReviewPreset/Presets/MedicalBackgroundPreset/MedicalBackgroundIndication.vue";
import { ChevronLeftIcon, CpuChipIcon, HandRaisedIcon, PlusIcon } from "@heroicons/vue/24/outline";
import Drawer from "@app/components/Global/Drawer/Drawer.vue";
import AssistantsSettings from "./ReviewPreset/AssistantsSettings/AssistantsSettings.vue";
import NotifiedBody from "./ReviewPreset/Presets/NotifiedBody/NotifiedBody.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import Loader from "@app/components/Loader.vue";

const props = defineProps<{
  projectId: Id;
  class?: string;
  afterReviewSeriesId?: string;
}>();

const isLoading = ref(false);
const loadingMessages = ref<string[]>([]);

const stepsComponents = {
  [CreateReviewStep.PresetSelection]: PresetSelection,
  [CreateReviewStep.PmsDeviceLevelMinimal]: DeviceSpecificSearchMinimalPreset,
  [CreateReviewStep.MedicalBackgroundIndication]: MedicalBackgroundIndication,
  [CreateReviewStep.Summary]: Summary,
  [CreateReviewStep.AssistantsSettings]: AssistantsSettings,
  [CreateReviewStep.NotifiedBody]: NotifiedBody,
};
const reviewCreate = useCreateProjectReview(props.projectId);

const currentStepComponent = computed(() => {
  return stepsComponents[reviewCreate.currentStep.value];
});

const projects = useProjects();
provide(CreateReviewKey, reviewCreate);
const snackbar = useSnackbar();
const loading = useLoading();
const router = useRouter();

function showCreateReviewDrawer(isAutomated: boolean, cb: () => void) {
  reviewCreate.isReviewAutomated.value = isAutomated;
  cb();
}

async function createReview(cb: () => void) {
  if (!reviewCreate.isCurrentStepValid.value) return;

  try {
    isLoading.value = true;
    const presetType = reviewCreate.preset.value.preset;
    let reviewId = 0;
    switch (presetType) {
      case ReviewPreset.MEDICAL_BACKGROUND:
        reviewId = await handleMedicalBackgroundPreset();
        break;

      case ReviewPreset.DEVICE_SPECIFIC_SEARCH:
        reviewId = await handleDevicePreset();
        break;
      case ReviewPreset.NOTIFIED_BODY:
        reviewId = await handleNotifiedBodyPreset();
        break;

      case ReviewPreset.CUSTOM:
      case ReviewPreset.DEFAULT:
      default:
        loading.start();
        reviewId = await reviewCreate.createReview(props.afterReviewSeriesId).finally(() => {
          loading.stop();
        });
    }
    if (!reviewId) throw new Error("Something went wrong, please try again");

    router.push(`/reviews/${reviewId}`);
    snackbar.success("Success");
  } catch (e) {
    handleError(e);
  } finally {
    cleanup(cb);
  }
}

async function handleMedicalBackgroundPreset() {
  reviewCreate.generateReviewName();
  const [_, reviewId] = await Promise.all([
    appendMessage("Creating review " + reviewCreate.reviewName.value),
    reviewCreate.createReview(props.afterReviewSeriesId),
  ]);
  await appendMessage("Generating plan");
  for await (const message of reviewCreate.autoImportMedicalBackgroundItems(reviewId)) {
    await appendMessage(message);
  }
  await appendSuccessMessage();
  return reviewId;
}

async function handleDevicePreset() {
  reviewCreate.generateReviewName();
  const [_, reviewId] = await Promise.all([
    appendMessage("Creating review " + reviewCreate.reviewName.value),
    reviewCreate.createReview(props.afterReviewSeriesId),
  ]);
  await appendMessage("Generating plan");
  for await (const message of reviewCreate.autoImportPmsItems({
    reviewId,
    safetyInformationSourceIds: reviewCreate.preset.value.safetyInformationSources,
  })) {
    await appendMessage(message);
  }
  await appendSuccessMessage();
  return reviewId;
}

async function handleNotifiedBodyPreset() {
  reviewCreate.generateReviewName();
  const [_, reviewId] = await Promise.all([
    appendMessage("Creating review " + reviewCreate.reviewName.value),
    reviewCreate.createReview(props.afterReviewSeriesId),
  ]);
  await appendMessage("Generating plan");
  for await (const message of reviewCreate.autoImportNotifiedBodyItems(reviewId)) {
    await appendMessage(message);
  }
  await appendSuccessMessage();
  return reviewId;
}

async function appendSuccessMessage() {
  await appendMessage("Review created successfully");
}

function handleError(e: unknown) {
  const error = e as HttpException;
  snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  if (error.response.data.statusCode >= 500) throw e;
}

function cleanup(cb: () => void) {
  isLoading.value = false;
  loadingMessages.value = [];
  reviewCreate.reset();
  projects.refresh();
  hideAll();
  cb();
}

async function hideDrawer() {
  await new Promise((resolve) => setTimeout(resolve, 700));
  reviewCreate.reset();
  loadingMessages.value = [];
  isLoading.value = false;
  hideAll();
}
async function appendMessage(message: string): Promise<void> {
  loadingMessages.value = [...loadingMessages.value, message];
}
</script>
<style>
.create-review-modal {
  height: min(800px, calc(100vh - 300px));
}
</style>
